/* eslint-disable react/jsx-no-bind */
// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, Tab, TabPanels, TabPanel, Box } from '@locus-taxy/neo-ui';

import { JsonFormsDispatch, useJsonForms } from '@jsonforms/react';
import { rankWith, uiTypeIs } from '@jsonforms/core';

import useLayoutProps, { getLayoutProps } from '../util/useLayoutProps';
import useActiveState from '../util/useActiveState';

type Props = {};

const useGetTabLayoutProps = ({ uischema, schema, path }) => {
    const ctx = useJsonForms();

    return uischema.elements.map(element => {
        return getLayoutProps(ctx, {
            uischema: element,
            schema,
            path
        });
    });
};

const TabLayoutRenderer = (props: Props): React.Node => {
    const { t } = useTranslation('platform');
    const {
        uischema,
        schema,
        path,
        visible,
        renderers,
        cells,
        enabled
    } = useLayoutProps(props);

    const tabLayoutProps = useGetTabLayoutProps({ uischema, schema, path });

    const visibleTabLayoutProps = tabLayoutProps.filter(
        ({ visible }) => visible
    );

    const [activeTab, setActivTab] = useActiveState({
        name: 'section',
        tabs: uischema.elements
    });

    return (
        visible && (
            <>
                <Box width="100%" backgroundColor="white" paddingX={4}>
                    <Tabs
                        value={activeTab}
                        onChange={(event, value) => setActivTab(value)}
                    >
                        {visibleTabLayoutProps.map((e, index) => (
                            <Tab
                                key={index}
                                value={index}
                                title={t(e.uischema.label)}
                            />
                        ))}
                    </Tabs>
                </Box>
                <Box
                    overflow="auto"
                    height="100%"
                    sx={{
                        '&::-webkit-scrollbar': {
                            borderRadius: '20px',
                            height: '10px',
                            width: '10px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#b3b3b3',
                            border: '3px solid #fff',
                            borderRadius: '20px'
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                            borderRadius: '20px'
                        }
                    }}
                >
                    <TabPanels value={activeTab} renderActiveOnly>
                        {visibleTabLayoutProps.map((e, index) => (
                            <TabPanel key={index} value={index} padding={4}>
                                {e.uischema.elements.map((element, index) => (
                                    <div key={`${path}-${index}`}>
                                        <JsonFormsDispatch
                                            uischema={element}
                                            schema={schema}
                                            path={path}
                                            enabled={enabled}
                                            renderers={renderers}
                                            cells={cells}
                                        />
                                    </div>
                                ))}
                            </TabPanel>
                        ))}
                    </TabPanels>
                </Box>
            </>
        )
    );
};

export default TabLayoutRenderer;

export const tabLayoutTester = rankWith(1, uiTypeIs('TabLayout'));
